import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { useSocketMessage } from 'services/api/updatesSocket';
import JobModal from '../../views/jobs/JobModal';
import ProposalModal from '../../views/proposals/ProposalModal';
import OrderModal from '../../views/order/OrderModal';
import InvoiceModal from '../../views/invoices/InvoiceModal';
import PurchaseModal from '../../views/purchases/PurchaseModal';

/*
 * useMentions.tsx
 * Loads the open mentions of the current user, refreshes them when a
 * colleague tags the user over the websocket and opens the page the
 * note was placed on. Shared by the notification bell and the
 * activities panel of the dashboard.
 */

// A mention of the current user as returned by the backend
export interface MentionType {
    id: number;
    created: string;
    read: boolean;
    mentioned_by_name: string | null;
    source: 'note' | 'ticket_note';
    text: string;
    page_type: string | null;
    page_id: number | null;
    page_name: string;
}

// Pages which open in a detail modal
const MENTION_MODAL_MAPPING: Record<string, React.FC<{ itemId?: number }>> = {
    job: JobModal,
    proposal: ProposalModal,
    order: OrderModal,
    invoice: InvoiceModal,
    purchase: PurchaseModal,
};

// Pages which open on their own url
const MENTION_URL_MAPPING: Record<string, string> = {
    organization: '/contacts/organization',
    person: '/contacts/person',
    location: '/contacts/work-locations',
    deal: '/deals',
    contract: '/contracts',
    product: '/products',
    ticket: '/tickets',
};

// Hook returning the open mentions of the user and the actions on them
export const useMentions = (enabled: boolean = true) => {
    const history = useHistory();
    const { initializeModal } = useModal();
    const authContext = useAuthContext();
    const [mentions, setMentions] = useState<MentionType[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const handleLogoutRef = useRef(authContext?.handleLogout);
    const { message } = useSocketMessage(['mention_created']);

    // Keep the logout handler in a ref so the fetch callback stays stable
    handleLogoutRef.current = authContext?.handleLogout;

    // Fetch the open mentions of the user
    const fetchMentions = useCallback(async () => {
        if (!enabled) return;
        try {
            const response = await fetchData({ apiUrl: 'get_mention_list', handleLogout: handleLogoutRef.current });
            setMentions(response?.results ?? []);
            setUnreadCount(response?.count ?? 0);
        } catch (error) {
            console.error('Error fetching mentions', error);
        }
    }, [enabled]);

    // Fetch the mentions on mount
    useEffect(() => {
        fetchMentions();
    }, [fetchMentions]);

    // Refresh the mentions when a colleague tags the user
    useEffect(() => {
        if (message) fetchMentions();
    }, [message, fetchMentions]);

    // Mark a mention as read and update the state
    const markMentionRead = async (mention: MentionType) => {
        if (mention.read) return;
        setMentions(current => current.map(item => item.id === mention.id ? { ...item, read: true } : item));
        setUnreadCount(current => Math.max(current - 1, 0));
        try {
            await saveData({ apiUrl: `mark_mention_read/${mention.id}`, method: 'post', data: {} });
        } catch (error) {
            console.error('Error marking mention as read', error);
        }
    };

    // Dismiss a mention so it disappears from the panels
    const dismissMention = async (mention: MentionType) => {
        setMentions(current => current.filter(item => item.id !== mention.id));
        if (!mention.read) setUnreadCount(current => Math.max(current - 1, 0));
        try {
            await saveData({ apiUrl: `dismiss_mention/${mention.id}`, method: 'post', data: {} });
        } catch (error) {
            console.error('Error dismissing mention', error);
        }
    };

    // Open the page the note of the mention is placed on and mark the mention as read
    const openMention = (mention: MentionType) => {
        markMentionRead(mention);
        if (!mention.page_type || !mention.page_id) return;

        // Open the detail modal of the page
        const ModalComponent = MENTION_MODAL_MAPPING[mention.page_type];
        if (ModalComponent) {
            initializeModal(<ModalComponent itemId={mention.page_id} />, { modalSize: 'large' });
            return;
        }

        // Navigate to the url of the page
        const url = MENTION_URL_MAPPING[mention.page_type];
        if (url) {
            history.push(`${url}/${mention.page_id}`);
        }
    };

    return { mentions, unreadCount, fetchMentions, openMention, dismissMention };
};
